import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, ExternalLink, X } from "lucide-react";

type Props = {
  modelId: string;
  modelName: string;
  onClose: () => void;
};

const SIZE_PRESETS = [
  { label: "Responsive", w: "100%", h: "560" },
  { label: "Small", w: "640", h: "400" },
  { label: "Medium", w: "960", h: "560" },
  { label: "Large", w: "1280", h: "720" },
];

/**
 * Modal that shows the iframe-embeddable snippet for `/embed/:id` along with
 * a one-click copy button and a few canned size presets. The embed URL is
 * always rooted at `window.location.origin` so it works for local dev and
 * production identically.
 */
export default function EmbedDialog({ modelId, modelName, onClose }: Props) {
  const [preset, setPreset] = useState(0);
  const [copied, setCopied] = useState<"snippet" | "url" | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  // Close on Escape so the dialog feels native.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const origin =
    typeof window !== "undefined" && window.location ? window.location.origin : "";
  const embedUrl = `${origin}/embed/${modelId}`;

  const { w, h } = SIZE_PRESETS[preset];
  const snippet = useMemo(
    () =>
      `<iframe
  src="${embedUrl}"
  title="${escapeHtml(modelName)} — Virtual Anatomy Lab"
  width="${w}"
  height="${h}"
  loading="lazy"
  allowfullscreen
  style="border:0;border-radius:12px;${w === "100%" ? "max-width:100%;" : ""}"
  referrerpolicy="no-referrer-when-downgrade"
></iframe>`,
    [embedUrl, modelName, w, h],
  );

  async function copy(text: string, kind: "snippet" | "url") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      ref.current?.select();
      document.execCommand("copy");
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-xl space-y-5 p-6 shadow-2xl ring-1 ring-slate-900/5"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Embed this model</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Paste this snippet into any page — the viewer loads inside an iframe with all
              labels and controls intact.
            </p>
          </div>
          <button onClick={onClose} className="btn-icon" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* Size presets */}
        <div>
          <div className="label-text">Size</div>
          <div className="flex flex-wrap gap-1.5">
            {SIZE_PRESETS.map((p, i) => (
              <button
                key={p.label}
                onClick={() => setPreset(i)}
                className={
                  preset === i
                    ? "rounded-full bg-brand-600 px-3 py-1 text-xs font-medium text-white shadow-sm"
                    : "rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200"
                }
              >
                {p.label}
                <span className="ml-1.5 text-[10px] opacity-70">
                  {p.w}×{p.h}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* URL row */}
        <div>
          <div className="label-text">Direct link</div>
          <div className="flex gap-2">
            <input value={embedUrl} readOnly className="input font-mono text-xs" />
            <button onClick={() => copy(embedUrl, "url")} className="btn-secondary">
              {copied === "url" ? <Check size={14} /> : <Copy size={14} />}
              {copied === "url" ? "Copied" : "Copy"}
            </button>
            <a href={embedUrl} target="_blank" rel="noreferrer" className="btn-ghost" title="Open">
              <ExternalLink size={14} />
            </a>
          </div>
        </div>

        {/* Snippet row */}
        <div>
          <div className="label-text">Iframe snippet</div>
          <textarea
            ref={ref}
            value={snippet}
            readOnly
            rows={8}
            className="textarea font-mono text-xs leading-relaxed"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button onClick={() => copy(snippet, "snippet")} className="btn-primary">
              {copied === "snippet" ? <Check size={14} /> : <Copy size={14} />}
              {copied === "snippet" ? "Copied!" : "Copy embed code"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" :
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === '"' ? "&quot;" :
    "&#39;",
  );
}
